import { useEffect, useState } from 'react';
import { ALERT_STYLE, TOGGLEABLE_ALERT_KINDS } from '../config/alerts';
import { useAppStore } from '../store/appStore';
import { AlertGlyph } from './AlertGlyph';
import { IconButton, Switch } from './ui';

const HILL_COUNTRY_COLOR = '#9c27b0';

/**
 * MapLayersPickerView: five alert toggles plus Texas Hill Country, whose info
 * button swaps the card for an explanation.
 */
export function LayerPicker() {
  const open = useAppStore((s) => s.layerPickerOpen);
  const setOpen = useAppStore((s) => s.setLayerPickerOpen);
  const toggles = useAppStore((s) => s.alertToggles);
  const setAlertToggle = useAppStore((s) => s.setAlertToggle);
  const hillCountryVisible = useAppStore((s) => s.hillCountryVisible);
  const setHillCountryVisible = useAppStore((s) => s.setHillCountryVisible);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (!open) setShowInfo(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={() => setOpen(false)} role="presentation">
      <div className="modal-card layer-picker" role="dialog" aria-modal="true" aria-label="Map layers" onClick={(e) => e.stopPropagation()}>
        {showInfo ? (
          <>
            <header className="modal-header">
              <span className="thc-swatch" aria-hidden="true" />
              <div className="modal-title-group">
                <h2 className="modal-title">Texas Hill Country</h2>
                <div className="modal-subtitle">Predicted Flooding Layer</div>
              </div>
              <IconButton label="Close info" onClick={() => setShowInfo(false)} className="modal-close">
                ×
              </IconButton>
            </header>
            <hr className="modal-divider" />
            <div className="thc-info">
              <p>The Texas Hill Country polygons represent the extent of predicted flooding in the area.</p>
              <p className="thc-info-row">
                <span className="thc-info-icon" aria-hidden="true">◐</span>
                The color of the polygon indicates the severity of the flood risk.
              </p>
              <p className="thc-info-row">
                <span className="thc-info-icon" aria-hidden="true">●</span>
                Darker shades indicate a greater risk of flooding; lighter shades indicate less risk.
              </p>
              <p className="thc-info-note">The current basins data set renders every watershed in a single shade.</p>
            </div>
          </>
        ) : (
          <>
            <header className="modal-header">
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l9 5-9 5-9-5 9-5z" fill="currentColor" />
                <path d="M3 12.5l9 5 9-5M3 16.5l9 5 9-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
              <h2 className="modal-title">Map Layers</h2>
              <IconButton label="Close map layers" onClick={() => setOpen(false)} className="modal-close">
                ×
              </IconButton>
            </header>
            <hr className="modal-divider" />
            <ul className="layer-rows">
              {TOGGLEABLE_ALERT_KINDS.map((k) => (
                <li key={k} className="layer-row">
                  <AlertGlyph kind={k} size={22} />
                  <span className="layer-row-label">{ALERT_STYLE[k].pluralLabel}</span>
                  <Switch checked={toggles[k]} onChange={(on) => setAlertToggle(k, on)} color={ALERT_STYLE[k].color} label={ALERT_STYLE[k].pluralLabel} />
                </li>
              ))}
              <li className="layer-row">
                <span className="thc-swatch" aria-hidden="true" />
                <span className="layer-row-label">
                  Texas Hill Country
                  <button type="button" className="info-btn" onClick={() => setShowInfo(true)} aria-label="About the Texas Hill Country layer">
                    i
                  </button>
                </span>
                <Switch checked={hillCountryVisible} onChange={setHillCountryVisible} color={HILL_COUNTRY_COLOR} label="Texas Hill Country" />
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
